import AxiosConfig from '../utils/AxiosConfig';

const PRODUCT_IMAGE_CACHE_KEY = 'product-image-preview-cache-v2';
const PRODUCT_IMAGE_CACHE_TTL_MS = 1000 * 60 * 60 * 12;

class ProductImagesService {
  constructor() {
    this.primaryImageCache = new Map();
    this.loadCacheFromSession();
  }

  loadCacheFromSession() {
    try {
      const raw = sessionStorage.getItem(PRODUCT_IMAGE_CACHE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      Object.entries(parsed).forEach(([productId, value]) => {
        this.primaryImageCache.set(Number(productId), value);
      });
    } catch (error) {
      this.primaryImageCache.clear();
    }
  }

  persistCache() {
    try {
      sessionStorage.setItem(
        PRODUCT_IMAGE_CACHE_KEY,
        JSON.stringify(Object.fromEntries(this.primaryImageCache.entries()))
      );
    } catch (error) {
      // Ignore storage issues.
    }
  }

  isFresh(entry) {
    return entry && Date.now() - entry.cachedAt < PRODUCT_IMAGE_CACHE_TTL_MS;
  }

  invalidateProductImageCache(productId) {
    this.primaryImageCache.delete(Number(productId));
    this.persistCache();
  }

  /**
   * Obtener todas las imágenes de un producto (dashboard)
   * GET /products/{productId}/images/admin
   */
  async getProductImages(productId) {
    try {
      const response = await AxiosConfig.get(`/products/${productId}/images/admin`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('[ProductImagesService] Error fetching product images:', error);
      throw error;
    }
  }

  /**
   * Subir una nueva imagen para un producto
   * POST /products/{productId}/images
   */
  async uploadImage(productId, file, displayOrder = 0, previewFile = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (previewFile) {
        formData.append('previewFile', previewFile);
      }
      
      const response = await AxiosConfig.post(
        `/products/${productId}/images?displayOrder=${displayOrder}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      this.invalidateProductImageCache(productId);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('[ProductImagesService] Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Eliminar una imagen
   * DELETE /products/images/{imageId}
   */
  async deleteImage(imageId, productId = null) {
    try {
      const response = await AxiosConfig.delete(`/products/images/${imageId}`);
      if (productId != null) {
        this.invalidateProductImageCache(productId);
      }
      return response.data?.data || response.data;
    } catch (error) {
      console.error('[ProductImagesService] Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Actualizar el orden de visualización de una imagen
   * PUT /products/images/{imageId}/order?displayOrder={order}
   */
  async updateDisplayOrder(imageId, displayOrder, productId = null) {
    try {
      const response = await AxiosConfig.put(
        `/products/images/${imageId}/order?displayOrder=${displayOrder}`
      );
      if (productId != null) {
        this.invalidateProductImageCache(productId);
      }
      return response.data?.data || response.data;
    } catch (error) {
      console.error('[ProductImagesService] Error updating display order:', error);
      throw error;
    }
  }

  async getPrimaryImageMap(productIds) {
    const uniqueProductIds = [...new Set((productIds || []).map(Number).filter(Boolean))];
    const now = Date.now();
    const result = {};
    const missingProductIds = [];

    uniqueProductIds.forEach((productId) => {
      const cached = this.primaryImageCache.get(productId);
      if (this.isFresh(cached) && cached.url) {
        result[productId] = cached.url;
      } else {
        if (cached) {
          this.primaryImageCache.delete(productId);
        }
        missingProductIds.push(productId);
      }
    });

    if (missingProductIds.length) {
      const response = await AxiosConfig.get('/products/images/primary/admin', {
        params: {
          productIds: missingProductIds.join(','),
        }
      });

      const entries = response.data?.data || response.data || [];
      entries.forEach((entry) => {
        const url = entry.previewUrl || entry.imageUrl || null;
        const productId = Number(entry.productId);
        if (!productId) return;

        result[productId] = url;
        if (url) {
          this.primaryImageCache.set(productId, {
            url,
            cachedAt: now,
          });
        } else {
          this.primaryImageCache.delete(productId);
        }
      });

      missingProductIds.forEach((productId) => {
        if (!(productId in result)) {
          result[productId] = null;
          this.primaryImageCache.delete(productId);
        }
      });

      this.persistCache();
    }

    return result;
  }
}

export default new ProductImagesService();
